/* iter-110 — Inline Area Editor (Ownership Deck quick-edit).

   Mounted from ProposedEnhancementsDialog when an admin/engineer hovers
   the small ✎ widget in the corner of any deck card.

   Two modes:
   • MANUAL   — directly edit name, description, all 6 ROI fields, and
                the enhancements list. Saves via PUT /api/admin/areas/{id}.
   • AI PROMPT — type a prompt like "make the investment range
                 conservative" and POST to /api/admin/ai/rewrite-area-description
                 with the prompt as `extra_brief`. Result is merged into the
                 form (user reviews + saves).

   Saving emits an `onSaved(updatedArea)` callback so the parent deck
   refreshes the affected card without a full reload.
*/
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Save, Loader2, Wand2, Pen, Sparkles, MessageSquare } from 'lucide-react';
import MoneyInput from './MoneyInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_KEYS = ['name', 'description', 'roi_scope', 'roi_investment', 'roi_return', 'roi_timeline', 'roi_benefits', 'enhancements_roi'];

export default function DeckCardEditor({ area, open, onClose, onSaved }) {
  const [mode, setMode] = useState('manual'); // 'manual' | 'ai'
  const [draft, setDraft] = useState(() => pickFields(area));
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;

  useEffect(() => {
    if (open) {
      setDraft(pickFields(area));
      setAiPrompt('');
      setMode('manual');
    }
  }, [open, area]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving && !aiBusy) onClose?.(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, aiBusy, onClose]);

  if (!open) return null;

  const change = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const runAi = async () => {
    if (!aiPrompt.trim()) { toast.error('Type what you want changed.'); return; }
    setAiBusy(true);
    try {
      // Use the existing rewrite endpoint with our prompt fed in as an
      // explicit instruction prepended to the source text. This avoids
      // touching the backend — we just compose the user prompt + the
      // current fields, ask the rewriter to apply the change, and write
      // the result into the draft.
      const { data } = await axios.post(
        `${API}/admin/ai/rewrite-area-description`,
        {
          area_id: area.id,
          save: false,
          extra_brief: aiPrompt.trim(),
        },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      const rewritten = data?.rewritten || {};
      // Tolerant pick — same trick as iter-110 AI ROI fix.
      const pick = (...keys) => {
        for (const k of keys) {
          if (rewritten[k] != null && String(rewritten[k]).trim() !== '') return rewritten[k];
        }
        return null;
      };
      setDraft((d) => ({
        ...d,
        name:             pick('name')             ?? d.name,
        description:      pick('description')      ?? d.description,
        roi_scope:        pick('roi_scope','scope') ?? d.roi_scope,
        roi_investment:   pick('roi_investment','investment') ?? d.roi_investment,
        roi_return:       pick('roi_return','return') ?? d.roi_return,
        roi_timeline:     pick('roi_timeline','timeline') ?? d.roi_timeline,
        roi_benefits:     pick('roi_benefits','benefits') ?? d.roi_benefits,
        enhancements_roi: pick('enhancements_roi','full_text','narrative') ?? d.enhancements_roi,
      }));
      toast.success('AI refined the fields · review then Save.');
      setMode('manual');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI rewrite failed.');
    } finally {
      setAiBusy(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/areas/${area.id}`,
        draft,
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      toast.success('Saved · deck card refreshed.');
      onSaved?.(data || { ...area, ...draft });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-end"
      style={{ background: 'rgba(8,15,22,0.7)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="deck-card-editor"
    >
      <div
        className="h-full w-full sm:w-[560px] flex flex-col"
        style={{ background: '#FAF8F3', boxShadow: '-12px 0 32px rgba(0,0,0,0.3)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E8DFC6' }}>
          <div className="flex items-center gap-2">
            <Pen className="w-4 h-4" style={{ color: '#1F4FBE' }} />
            <h2 className="text-sm font-bold" style={{ fontFamily: 'Manrope', color: '#0A1A2F' }}>Edit deck card</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving || aiBusy}
            data-testid="deck-card-editor-close"
            className="w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-40"
            style={{ background: 'rgba(0,0,0,0.06)', color: '#1A2E2E' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex border-b" style={{ borderColor: '#E8DFC6' }}>
          {[
            { key: 'manual', label: 'Manual', icon: Pen },
            { key: 'ai', label: 'AI prompt', icon: Sparkles },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = mode === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setMode(tab.key)}
                data-testid={`deck-card-editor-tab-${tab.key}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 text-xs font-bold"
                style={{
                  fontFamily: 'Manrope',
                  color: active ? '#1F4FBE' : '#7A8C8C',
                  borderBottom: active ? '2px solid #1F4FBE' : '2px solid transparent',
                  background: active ? 'rgba(31,79,190,0.06)' : 'transparent',
                }}
              >
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {mode === 'manual' && (
            <>
              <FieldGroup label="Name">
                <input
                  data-testid="deck-edit-name"
                  className={INPUT}
                  style={INPUT_STYLE}
                  value={draft.name || ''}
                  onChange={(e) => change('name', e.target.value)}
                />
              </FieldGroup>
              <FieldGroup label="Description">
                <textarea
                  data-testid="deck-edit-description"
                  rows={3}
                  className={INPUT}
                  style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 84 }}
                  value={draft.description || ''}
                  onChange={(e) => change('description', e.target.value)}
                />
              </FieldGroup>

              <FieldGroup label="Scope">
                <textarea
                  data-testid="deck-edit-roi-scope"
                  rows={2}
                  className={INPUT}
                  style={{ ...INPUT_STYLE, resize: 'vertical' }}
                  value={draft.roi_scope || ''}
                  onChange={(e) => change('roi_scope', e.target.value)}
                />
              </FieldGroup>
              <FieldGroup label="Investment">
                <MoneyInput
                  data-testid="deck-edit-roi-investment"
                  className={INPUT}
                  style={INPUT_STYLE}
                  value={draft.roi_investment || ''}
                  onChange={(e) => change('roi_investment', e.target.value)}
                />
              </FieldGroup>
              <FieldGroup label="Return">
                <MoneyInput
                  data-testid="deck-edit-roi-return"
                  className={INPUT}
                  style={INPUT_STYLE}
                  value={draft.roi_return || ''}
                  onChange={(e) => change('roi_return', e.target.value)}
                />
              </FieldGroup>
              <FieldGroup label="Timeline">
                <input
                  data-testid="deck-edit-roi-timeline"
                  className={INPUT}
                  style={INPUT_STYLE}
                  value={draft.roi_timeline || ''}
                  onChange={(e) => change('roi_timeline', e.target.value)}
                />
              </FieldGroup>
              <FieldGroup label="Strategic Benefits">
                <textarea
                  data-testid="deck-edit-roi-benefits"
                  rows={2}
                  className={INPUT}
                  style={{ ...INPUT_STYLE, resize: 'vertical' }}
                  value={draft.roi_benefits || ''}
                  onChange={(e) => change('roi_benefits', e.target.value)}
                />
              </FieldGroup>
              <FieldGroup label="ROI Narrative (red glow box)">
                <textarea
                  data-testid="deck-edit-enhancements-roi"
                  rows={6}
                  className={INPUT}
                  style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 130 }}
                  value={draft.enhancements_roi || ''}
                  onChange={(e) => change('enhancements_roi', e.target.value)}
                />
              </FieldGroup>
            </>
          )}

          {mode === 'ai' && (
            <div className="space-y-3">
              <FieldGroup label="Tell the AI what to change">
                <textarea
                  data-testid="deck-edit-ai-prompt"
                  rows={5}
                  placeholder={'Examples:\n  • "Make the investment range more conservative"\n  • "Reduce the timeline by 2 months and adjust ROI accordingly"\n  • "Rewrite the narrative for a Hyatt board presentation"\n  • "Add an emphasis on sustainability without changing the numbers"'}
                  className={INPUT}
                  style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 140 }}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                />
              </FieldGroup>
              <button
                type="button"
                onClick={runAi}
                disabled={aiBusy || !aiPrompt.trim()}
                data-testid="deck-edit-ai-go"
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', fontFamily: 'Manrope' }}
              >
                {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {aiBusy ? 'Refining…' : 'Refine with AI'}
              </button>
              <div className="rounded-xl px-3 py-2 text-[11px]" style={{ background: 'rgba(31,79,190,0.06)', color: '#1F4FBE', fontFamily: 'Manrope' }}>
                <MessageSquare className="w-3 h-3 inline mr-1 -mt-0.5" />
                After AI refines, you&apos;ll review the values in the Manual tab and click Save.
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center gap-2" style={{ borderColor: '#E8DFC6' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving || aiBusy}
            className="px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-40"
            style={{ background: 'rgba(0,0,0,0.06)', color: '#1A2E2E', fontFamily: 'Manrope' }}
          >
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || aiBusy}
            data-testid="deck-edit-save"
            className="px-5 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #22C55E 0%, #15803D 100%)', fontFamily: 'Manrope' }}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : <Save className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function pickFields(a) {
  const out = {};
  for (const k of FIELD_KEYS) out[k] = a?.[k] ?? '';
  return out;
}

function FieldGroup({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{label}</span>
      {children}
    </label>
  );
}

const INPUT = 'w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-300';
const INPUT_STYLE = { background: 'white', border: '1px solid #E8DFC6', color: '#1A2E2E', fontFamily: 'Manrope' };
